import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCircleCheck } from '@fortawesome/free-solid-svg-icons';

import { Reveal } from '../../../../components/Reveal/Reveal';
import { SectionHeader } from '../../../../components/SectionHeader/SectionHeader';

import ui from '../../../../styles/ui.module.css';
import st from './Program.module.css';

function moduleCountWord(n) {
  const abs = Math.abs(n) % 100;
  const rem = abs % 10;
  if (rem === 1 && abs !== 11) return 'модуль';
  if (rem >= 2 && rem <= 4 && (abs < 12 || abs > 14)) return 'модуля';
  return 'модулей';
}

function ModuleBody({ open, id, label, children }) {
  const reduceMotion = useReducedMotion();
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setHeight(el.scrollHeight + 10 || 0);

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    if (!el) return;
    setHeight(el.scrollHeight + 10 || 0);
  }, [open]);

  return (
    <motion.div
      id={id}
      role="region"
      aria-label={label}
      className={st.body}
      initial={false}
      animate={
        open
          ? { height, opacity: 1, y: 0, pointerEvents: 'auto' }
          : { height: 0, opacity: 0, y: -6, pointerEvents: 'none' }
      }
      transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: 'easeInOut' }}
    >
      <div ref={contentRef} className={st.bodyInner}>
        {children}
      </div>
    </motion.div>
  );
}

export function Program({ tracks, subtitle }) {
  const accId = useId();
  const [activeTrackId, setActiveTrackId] = useState(tracks[0]?.id ?? '');
  const [activeModule, setActiveModule] = useState(1);

  const activeTrack = useMemo(
    () => tracks.find((t) => t.id === activeTrackId) ?? tracks[0],
    [tracks, activeTrackId]
  );

  const moduleCount = activeTrack?.modules?.length ?? 0;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setActiveModule(0);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setActiveModule(1);
  }, [activeTrackId]);

  if (!activeTrack) return null;

  return (
    <section className={`${ui.section} ${ui.sectionSeam} ${st.root}`}>
      <div className={ui.container}>
        <Reveal>
          <SectionHeader
            kicker="Программа"
            title="Модули обучения"
            subtitle={subtitle}
          />
        </Reveal>

        <Reveal delay={0.04}>
          <div className={st.trackTabs} role="tablist" aria-label="Специализация">
            {tracks.map((t) => {
              const selected = t.id === activeTrackId;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`${st.trackTab} ${selected ? st.trackTabActive : ''}`}
                  onClick={() => setActiveTrackId(t.id)}
                >
                  {t.audienceLabel}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className={st.trackMeta}>
          <span className={st.trackMetaLabel}>Текущая модель</span>
          <span className={st.trackMetaValue}>
            {activeTrack.audienceLabel} · {moduleCount} {moduleCountWord(moduleCount)}
          </span>
        </div>

        <div className={st.modules}>
          {activeTrack.modules.map((m) => {
            const open = activeModule === m.id;
            const panelId = `${accId}-${activeTrack.id}-module-panel-${m.id}`;
            return (
              <Reveal key={`${activeTrack.id}-${m.id}`} delay={Math.min(m.id * 0.015, 0.18)}>
                <button
                  className={`${ui.card} ${st.module} ${open ? st.open : ''}`}
                  type="button"
                  onClick={() => setActiveModule(open ? 0 : m.id)}
                  aria-expanded={open}
                  aria-controls={panelId}
                >
                  <div className={st.row}>
                    <div className={st.left}>
                      <div className={st.badge}>Модуль {m.id}</div>
                      <div className={st.title}>
                        <span className={st.icon} aria-hidden="true">
                          <FontAwesomeIcon icon={m.icon} />
                        </span>
                        <span>{m.title}</span>
                      </div>
                    </div>
                    <div className={st.right}>
                      <span className={st.hint}>{open ? 'Свернуть' : 'Подробнее'}</span>
                      <span className={st.chev} aria-hidden="true">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </span>
                    </div>
                  </div>

                  <ModuleBody open={open} id={panelId} label={`Модуль ${m.id}: ${m.title}`}>
                    <div className={st.divider} />
                    <ul className={st.bullets}>
                      {m.points.map((p) => (
                        <li key={p} className={st.bulletItem}>
                          <FontAwesomeIcon icon={faCircleCheck} className={st.bulletIcon} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </ModuleBody>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
